import { createContext, useContext, useState, useCallback, useRef } from 'react';
import { runEmailHealthCheck, getClients, getEmailHealth, SpamHeroCheckResult, SpamHeroHealthSummary } from '../services/api';

interface RunAllProgress {
  current: number;
  total: number;
  clientName: string;
}

interface EmailHealthContextType {
  // Run All state
  runAllLoading: boolean;
  runAllProgress: RunAllProgress | null;
  // Individual client running state
  runningClients: Set<string>;
  // Cached results (persist across navigation)
  checks: SpamHeroCheckResult[];
  summary: SpamHeroHealthSummary | null;
  loading: boolean;
  error: string | null;
  // Actions
  startRunAll: () => void;
  runSingleCheck: (clientId: string) => Promise<void>;
  fetchData: () => Promise<void>;
}

const EmailHealthContext = createContext<EmailHealthContextType | null>(null);

export function EmailHealthProvider({ children }: { children: React.ReactNode }) {
  const [runAllLoading, setRunAllLoading] = useState(false);
  const [runAllProgress, setRunAllProgress] = useState<RunAllProgress | null>(null);
  const [runningClients, setRunningClients] = useState<Set<string>>(new Set());
  const [checks, setChecks] = useState<SpamHeroCheckResult[]>([]);
  const [summary, setSummary] = useState<SpamHeroHealthSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const runAllAbort = useRef(false);
  const dataFetched = useRef(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getEmailHealth();
      setSummary(data.summary);
      setChecks(data.checks);
      dataFetched.current = true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load email health data');
    } finally {
      setLoading(false);
    }
  }, []);

  const runSingleCheck = useCallback(async (clientId: string) => {
    setRunningClients(prev => new Set(prev).add(clientId));
    try {
      const result = await runEmailHealthCheck(clientId);
      setChecks(prev => {
        const exists = prev.some(c => c.clientId === clientId);
        if (exists) return prev.map(c => c.clientId === clientId ? result : c);
        return [...prev, result];
      });
      await fetchData();
    } finally {
      setRunningClients(prev => {
        const next = new Set(prev);
        next.delete(clientId);
        return next;
      });
    }
  }, [fetchData]);

  const startRunAll = useCallback(async () => {
    if (runAllLoading) return;
    setRunAllLoading(true);
    runAllAbort.current = false;

    try {
      const clientData = await getClients({ pageSize: 500 });
      const allClients = clientData.items;

      for (let i = 0; i < allClients.length; i++) {
        if (runAllAbort.current) break;

        const client = allClients[i];
        setRunAllProgress({ current: i + 1, total: allClients.length, clientName: client.companyName });
        setRunningClients(prev => new Set(prev).add(client.id));

        try {
          const result = await runEmailHealthCheck(client.id);
          setChecks(prev => {
            const exists = prev.some(c => c.clientId === client.id);
            if (exists) return prev.map(c => c.clientId === client.id ? result : c);
            return [...prev, result];
          });
        } catch {
          // continue to next client
        } finally {
          setRunningClients(prev => {
            const next = new Set(prev);
            next.delete(client.id);
            return next;
          });
        }
      }

      await fetchData();
    } finally {
      setRunAllLoading(false);
      setRunAllProgress(null);
    }
  }, [runAllLoading, fetchData]);

  return (
    <EmailHealthContext.Provider value={{
      runAllLoading,
      runAllProgress,
      runningClients,
      checks,
      summary,
      loading,
      error,
      startRunAll,
      runSingleCheck,
      fetchData,
    }}>
      {children}
    </EmailHealthContext.Provider>
  );
}

export function useEmailHealth() {
  const ctx = useContext(EmailHealthContext);
  if (!ctx) throw new Error('useEmailHealth must be used within EmailHealthProvider');
  return ctx;
}
